var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ImageTypeNameEnum } from '@inter-ikea-kompis/enums';
import { DexfSettingsUtility, ProductDescriptionProductMeasureEnum } from '@inter-ikea-kompis/utilities';
import { ImageAspectRatioEnum } from '@inter-ikea-kompis/component-image';
import { ZipInCardStateEnum } from '@inter-ikea-kompis/component-zip-in';
import '../information-card/InformationCard.js';
import '../list-gallery-card/ListGalleryCard.js';
import ListGalleryCardMediaEnum from '../../enums/ListGalleryCardMediaEnum.js';
import ProductCardInfoIconButtonLinkTargetEnum from '../../enums/ProductCardInfoIconButtonLinkTargetEnum.js';
import ProductCardLayoutEnum from '../../enums/ProductCardLayoutEnum.js';
import ProductCardMediaEnum from '../../enums/ProductCardMediaEnum.js';
import ProductCardPriceLayoutTypeEnum from '../../enums/ProductCardPriceLayoutTypeEnum.js';
import ProductCardSelectBehaviourEnum from '../../enums/ProductCardSelectBehaviourEnum.js';
import ProductCardSelectedStateEnum from '../../enums/ProductCardSelectedStateEnum.js';
import ProductCardTracker from '../../trackers/ProductCardTracker.js';
import InformationCard from '../information-card/InformationCard.js';
import ListGalleryCard from '../list-gallery-card/ListGalleryCard.js';
/**
 * Product card component.
 *
 * @event modalClose Triggered when the modal should be closed.
 * @event modalOpen Triggered when a modal should be opened. "event.detail.visibleModal" tells you what modal that should be opened.
 * @event carouselChange Triggered when a carousel slide should be changed. Only available when layout is set to "information".
 * @event carouselShowAllThumbnails Triggered when the show all thumbnails button is clicked in the carousel. Only available when layout is set to "information".
 * @event productVariantClick Triggered when a product variant thumbnail is clicked. Only available when layout is set to "gallery".
 * @event productVariantShowMoreClick Triggered when the show more variants button is clicked. Only available when layout is set to "gallery".
 * @event infoIconButtonClick Triggered when the info icon button is clicked. Only available when layout is set to "list".
 * @event select Triggered when the user tabs to the card and selects it by pressing the Enter or the Space key. Only available when layout is set to "list" or "gallery".
 * @event storeSelectorChange Triggered when the user selects a store in the store selector. Only available when layout is set to "information".
 * @event homeDeliveryForgetZipCode User clicks on the forget button in Home Delivery.
 * @event homeDeliverySaveZipCode User clicks on the save link from Home Delivery.
 * @event homeDeliveryClick User clicks to open zip in sheet.
 */
export default class ProductCard extends KompisElement {
    constructor() {
        super(...arguments);
        // Defines the overall layout of the card.
        this.layout = ProductCardLayoutEnum.list;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Optional array of function icons.
        this.functionIcons = null;
        // Optional array of type IProduct which will be displayed as "More options available". Only available when layout is set to "gallery" or "list".
        this.productVariants = null;
        // Optional array of type IProduct used for variations of a product when card should represent multiple variations, i.e for auto adapt products. Price of cheapest variation will be shown as "from price". Only available when layout is set to "list".
        this.productVariations = null;
        // Product variants limit. Only available when layout is set to "gallery" or "list".
        this.productVariantsLimit = null;
        // Optional array of type IProduct used for products included in the main product, i.e. child products. Needed for price to be calculated and displayed for an assembly product (ASP). Products included in all variations is needed when card should represent multiple variations of an assembly product.
        this.includedProducts = null;
        // Store availability for the product(s). Object or array. Array is used when card should represent multiple variations of a product (array with availability for all variations), an assembly product (ASP) (array of availabilty for all child items) or multiple variations of assembly products (array of availabilty for all child items of all variations).
        this.storeAvailability = null;
        // Zip availability for the product(s). Object or array. Array is used when card should represent multiple variations of a product (array with availability for all variations), an assembly product (ASP) (array of availabilty for all child items) or multiple variations of assembly products (array of availabilty for all child items of all variations).
        this.zipAvailability = null;
        // Show a modal of type ProductCardModalEnum.
        this.visibleModal = null;
        // Sets how media should be displayed. The carousel option is only available when layout is set to "information".
        this.media = ProductCardMediaEnum.singleImage;
        // Selected index of the carousel slides. Only available when layout is set to "information".
        this.carouselSelectedIndex = 0;
        // Max limit of thumbnails to be displayed in the carousel. Only available when layout is set to "information".
        this.carouselThumbnailLimit = null;
        // Shows all thumbnails in the carousel when a limit has been set. Only available when layout is set to "information".
        this.carouselShowAll = false;
        // Show scrollbar in carousel. Only available when layout is set to "information".
        this.carouselShowScrollbar = false;
        // Show arrows in carousel. Only available when layout is set to "information".
        this.carouselShowArrows = false;
        // Info icon button link URL. Only available when layout is set to "list".
        this.infoIconButtonLinkUrl = null;
        // Info icon button link target. Only available when layout is set to "list".
        this.infoIconButtonLinkTarget = ProductCardInfoIconButtonLinkTargetEnum.self;
        // Selected state. Only available when layout is set to "list".
        this.selectedState = ProductCardSelectedStateEnum.unselected;
        // When enabled it is possible select the product card using keyboard or mouse. Set selected state with the "selectedState" attribute when using the "list" layout. Only available when layout is set to "list" or "gallery"
        this.selectBehaviour = ProductCardSelectBehaviourEnum.disabled;
        // Product measures. Only affects measurements in the product description. Measurement action item will be rendered based on article data.
        this.productMeasures = ProductDescriptionProductMeasureEnum.none;
        // Price module layout type.
        this.priceModuleLayout = ProductCardPriceLayoutTypeEnum.default;
        // Show product package measures.
        this.showPackageMeasures = false;
        // Show text describing the color and/or material of the product.
        this.showDesignText = false;
        // Show product package weight info.
        this.showWeight = false;
        // Shows a label that tells the customer that there are more options available. Only available when layout is set to "list" or "gallery".
        this.showProductVariants = false;
        // Shows a product identifier if the layout is set to "information". Only available when layout is set to "information".
        this.showProductIdentifier = false;
        // Shows an info icon button. Only available when layout is set to "list".
        this.showInfoIconButton = false;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Show price excl tax.
        this.showPriceExclTax = false;
        // Stores. Only available when layout is set to "information".
        this.stores = null;
        // Selected store id. Only available when layout is set to "information".
        this.storeId = null;
        // Text for product description. Only available when layout is set to "information".
        this.productDescription = null;
        // The aspect ratio that the images should adopt.
        this.imageAspectRatio = ImageAspectRatioEnum.ORIGINAL;
        // Shows the Home delivery component.
        this.showHomeDelivery = false;
        // Card state for Home Delivery
        this.homeDeliveryState = ZipInCardStateEnum.default;
        // Submitted and validated ZIP code for Home Delivery.
        this.homeDeliveryZipCode = null;
        // Defines the image sort order. When only one image is being displayed, the first image matching the type is used.
        this.imageSortOrder = null;
        // Defines the image sort order for the image displayed on hover when "media" is set to "switchImageOnHover".
        this.hoverImageSortOrder = [
            ImageTypeNameEnum.functionalProductPicture,
            ImageTypeNameEnum.contextProductPicture
        ];
        // Aria label that will be read up together with the product name and description. Only rendered when selectedState = checkbox. Tells the user that, when navigating to the product card, it has the "checkbox icon". What that means depends on how you have implemented it in your planner.
        this.selectedStateAriaLabel = null;
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
        // Hides the text describing the type of the product. Can be turned on when e.g. listing the same product type in a list to avoid redundant information. Please note that it should be clear to the user what type of products are listed when this is turned on.
        this.hideTypeName = false;
        this.tracker = null;
        this.dexfSettings = null;
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        if (!this.product.content) {
            return null;
        }
        switch (this.layout) {
            case ProductCardLayoutEnum.information:
                return html `${this.getInformationCard()}`;
            case ProductCardLayoutEnum.gallery:
            case ProductCardLayoutEnum.list:
            default:
                return html `${this.getListGalleryCard()}`;
        }
    }
    connectedCallback() {
        this.customElements.define('kompis-information-card', InformationCard);
        this.customElements.define('kompis-list-gallery-card', ListGalleryCard);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.disconnectTracker();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('analytics') && this.hasTranslationsAndSettings()) {
            this.disconnectTracker();
            this.connectTracker();
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return !!this.localizedInformation || !!(this.translations && this.settings);
    }
    getSettings() {
        return this.dexfSettings;
    }
    async connectTracker() {
        if (this.analytics) {
            this.tracker = new ProductCardTracker({
                ...this.analytics,
                settings: this.getSettings()
            });
            await this.tracker?.connect();
        }
    }
    disconnectTracker() {
        this.tracker?.disconnect();
        this.tracker = null;
    }
    getInformationCard() {
        return html `<kompis-information-card
			@carouselChange="${(event) => {
            this.dispatchEvent(new CustomEvent('carouselChange', { bubbles: true, detail: event.detail }));
            this.tracker?.sendCarouselChange(event.detail);
        }}"
			@carouselShowAllThumbnails="${(event) => {
            this.dispatchEvent(new CustomEvent('carouselShowAllThumbnails', { bubbles: true, detail: event.detail }));
            this.tracker?.sendCarouselShowAllThumbnails(event.detail.carouselShowAll);
        }}"
			@storeSelectorChange="${(event) => {
            this.dispatchEvent(new CustomEvent('storeSelectorChange', {
                bubbles: true,
                detail: event.detail
            }));
            if (this.product.content?.ruItemNo) {
                this.tracker?.sendStoreSelectorChange({
                    storeId: event.detail.storeId,
                    product: this.product.content.ruItemNo
                });
            }
        }}"
			@modalClose="${(event) => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
			@modalOpen="${(event) => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: event.detail
        }))}"
			@homeDeliveryForgetZipCode="${(event) => {
            this.dispatchEvent(new CustomEvent('homeDeliveryForgetZipCode', {
                bubbles: true,
                detail: { ...event.detail }
            }));
        }}"
			@homeDeliverySaveZipCode="${(event) => {
            this.dispatchEvent(new CustomEvent('homeDeliverySaveZipCode', { bubbles: true, detail: { ...event.detail } }));
        }}"
			@homeDeliveryClick="${(event) => {
            this.dispatchEvent(new CustomEvent('homeDeliveryClick', { bubbles: true, detail: { ...event.detail } }));
        }}"
			.theme="${this.theme}"
			.product="${this.product}"
			.includedProducts="${this.includedProducts}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.media="${this.media}"
			.imageAspectRatio="${this.imageAspectRatio}"
			.imageSortOrder="${this.imageSortOrder}"
			.hoverImageSortOrder="${this.hoverImageSortOrder}"
			.carouselSelectedIndex="${this.carouselSelectedIndex}"
			.carouselThumbnailLimit="${this.carouselThumbnailLimit}"
			.carouselShowAll="${this.carouselShowAll}"
			.carouselShowScrollbar="${this.carouselShowScrollbar}"
			.carouselShowArrows="${this.carouselShowArrows}"
			.functionIcons="${this.functionIcons}"
			.priceModuleLayout="${this.priceModuleLayout}"
			.visibleModal="${this.visibleModal}"
			.disableLinks="${this.disableLinks}"
			.showPriceExclTax="${this.showPriceExclTax}"
			.productMeasures="${this.productMeasures}"
			.showDesignText="${this.showDesignText}"
			.showWeight="${this.showWeight}"
			.showPackageMeasures="${this.showPackageMeasures}"
			.showHomeDelivery="${this.showHomeDelivery}"
			.zipAvailability="${this.zipAvailability}"
			.homeDeliveryState="${this.homeDeliveryState}"
			.homeDeliveryZipCode="${this.homeDeliveryZipCode}"
			.storeAvailability="${this.storeAvailability}"
			.stores="${this.stores}"
			.showProductIdentifier="${this.showProductIdentifier}"
			.productDescription="${this.productDescription}"
			.storeId="${this.storeId}"
			.measurementSystem="${this.measurementSystem}"
			.hideTypeName="${this.hideTypeName}"
		>
			<slot></slot>
		</kompis-information-card>`;
    }
    getListGalleryCard() {
        return html `
			<kompis-list-gallery-card
				@modalOpen="${(event) => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: event.detail
        }))}"
				@modalClose="${(event) => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
				@infoIconButtonClick="${(event) => {
            this.dispatchEvent(new CustomEvent('infoIconButtonClick', {
                bubbles: true,
                detail: event.detail
            }));
            if (this.product.content?.ruItemNo &&
                (this.disableLinks || !this.infoIconButtonLinkUrl)) {
                this.tracker?.sendInfoIconButtonClick(this.product.content.ruItemNo);
            }
        }}"
				@productVariantClick="${(event) => {
            this.dispatchEvent(new CustomEvent('productVariantClick', { bubbles: true, detail: event.detail }));
            this.tracker?.sendProductVariantClick(event.detail.product.content.ruItemNo);
        }}"
				@productVariantShowMoreClick="${(event) => {
            this.dispatchEvent(new CustomEvent('productVariantShowMoreClick', {
                bubbles: true,
                detail: event.detail
            }));
            if (this.product.content?.ruItemNo) {
                this.tracker?.sendProductVariantShowMoreClick(this.product.content.ruItemNo);
            }
        }}"
				@select="${(event) => {
            this.dispatchEvent(new CustomEvent('select', {
                bubbles: true,
                detail: event.detail
            }));
            if (event.detail?.selectedState) {
                this.tracker?.sendSelectedProduct(event.detail.selectedState);
            }
        }}"
				.theme="${this.theme}"
				.media="${this.media === ProductCardMediaEnum.carousel
            ? ListGalleryCardMediaEnum.singleImage
            : this.media}"
				.product="${this.product}"
				.includedProducts="${this.includedProducts}"
				.layout="${this.layout}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.functionIcons="${this.functionIcons}"
				.imageAspectRatio="${this.imageAspectRatio}"
				.imageSortOrder="${this.imageSortOrder}"
				.hoverImageSortOrder="${this.hoverImageSortOrder}"
				.disableLinks="${this.disableLinks}"
				.infoIconButtonLinkUrl="${this.infoIconButtonLinkUrl}"
				.showInfoIconButton="${this.showInfoIconButton}"
				.infoIconButtonLinkTarget="${this.infoIconButtonLinkTarget}"
				.productVariations="${this.productVariations}"
				.priceModuleLayout="${this.priceModuleLayout}"
				.selectBehaviour="${this.selectBehaviour}"
				.visibleModal="${this.visibleModal}"
				.productMeasures="${this.productMeasures}"
				.showDesignText="${this.showDesignText}"
				.showWeight="${this.showWeight}"
				.selectedStateAriaLabel="${this.selectedStateAriaLabel}"
				.showPriceExclTax="${this.showPriceExclTax}"
				.showPackageMeasures="${this.showPackageMeasures}"
				.selectedState="${this.selectedState}"
				.zipAvailability="${this.zipAvailability}"
				.storeAvailability="${this.storeAvailability}"
				.stores="${this.stores}"
				.storeId="${this.storeId}"
				.showProductVariants="${this.showProductVariants}"
				.productVariants="${this.productVariants}"
				.productVariantsLimit="${this.productVariantsLimit}"
				.measurementSystem="${this.measurementSystem}"
				.hideTypeName="${this.hideTypeName}"
			></kompis-list-gallery-card>
		`;
    }
}
__decorate([
    property({ type: String })
], ProductCard.prototype, "layout", void 0);
__decorate([
    property({ type: Object, required: true })
], ProductCard.prototype, "product", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "settings", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "functionIcons", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "productVariants", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "productVariations", void 0);
__decorate([
    property({ type: Number })
], ProductCard.prototype, "productVariantsLimit", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "includedProducts", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "storeAvailability", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "zipAvailability", void 0);
__decorate([
    property({ type: Object, required: true })
], ProductCard.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "visibleModal", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "media", void 0);
__decorate([
    property({ type: Number })
], ProductCard.prototype, "carouselSelectedIndex", void 0);
__decorate([
    property({ type: Number })
], ProductCard.prototype, "carouselThumbnailLimit", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "carouselShowAll", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "carouselShowScrollbar", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "carouselShowArrows", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "infoIconButtonLinkUrl", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "infoIconButtonLinkTarget", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "selectedState", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "selectBehaviour", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "productMeasures", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "priceModuleLayout", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "showPackageMeasures", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "showDesignText", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "showWeight", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "showProductVariants", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "showProductIdentifier", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "showInfoIconButton", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "showPriceExclTax", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "stores", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "storeId", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "productDescription", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "imageAspectRatio", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "showHomeDelivery", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "homeDeliveryState", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "homeDeliveryZipCode", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "imageSortOrder", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "hoverImageSortOrder", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "selectedStateAriaLabel", void 0);
__decorate([
    property({ type: Object })
], ProductCard.prototype, "analytics", void 0);
__decorate([
    property({ type: String })
], ProductCard.prototype, "measurementSystem", void 0);
__decorate([
    property({ type: Boolean })
], ProductCard.prototype, "hideTypeName", void 0);
//# sourceMappingURL=ProductCard.js.map